import React, { useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from 'ui-library';

import { useInvestorProfile } from 'components/InvestorProfileBase/hooks/useInvestorProfile';
import { InvestorProfileBase } from 'components/InvestorProfileBase/InvestorProfileBase';
import { useCreatePortfolioSelectors } from 'datasource/CreatePortfolio';

import pencilIcon from 'clientRequirements/images/pencilIcon.png';
import './InvestorProfile.css';

// --- Interfaces ---

interface InvestorProfileGroup {
    Name?: string;
    [key: string]: any;
}

// Assuming the shape of the object returned by your custom hook
interface InvestorProfileHookResult {
    data: any;
    formData: Record<string, any>;
    groupId: string | number;
    isLoading: boolean;
    clientId: string | number;
    isEditMode: boolean;
    expirationDate?: string;
    errors: Record<string, string>;
    investorProfileGroup: InvestorProfileGroup;
    isDataChanged: () => boolean;
    showInvestorProfileEditView: () => void;
    onChange: (val: any) => void;
    // Add other methods used in your dependency array
    postQuestionnaire?: () => void;
    calculateRisk?: () => void;
    setChosenRisk?: (risk: any) => void;
}

interface InvestorProfileProps {
    dfsClientId: string | number;
    clientAge: number;
    clientName: string;
    title?: string;
    isSubmitted: boolean;
    isSending: boolean;
    onQuestionnaireUpdate?: (id: string | number, profile: InvestorProfileHookResult) => void;
}

export const InvestorProfile: React.FC<InvestorProfileProps> = ({
    dfsClientId,
    clientAge,
    clientName,
    title,
    isSubmitted,
    isSending,
    onQuestionnaireUpdate,
}) => {
    const { t } = useTranslation();
    const { advisoryDataSelector } = useCreatePortfolioSelectors();

    const investorProfile = useInvestorProfile({
        dfsClientId,
        clientAge,
        advisoryDataSelector,
    }) as InvestorProfileHookResult;

    useEffect(() => {
        // This call is the primary suspect for the infinite loop if the parent re-renders
        onQuestionnaireUpdate?.(dfsClientId, investorProfile);

        // const isQChanged = investorProfile?.isDataChanged();
        if (false) {
            investorProfile?.showInvestorProfileEditView();
        }
    }, [
        // Reduce this list to only essential data.
        // If functions aren't wrapped in useCallback inside the hook, they cause loops.
        investorProfile?.data,
        investorProfile?.formData,
        investorProfile?.groupId,
        investorProfile?.isDataChanged,
        investorProfile?.isLoading,
        investorProfile?.clientId,
        onQuestionnaireUpdate,
        dfsClientId,
    ]);

    return (
        <div className="new-portfolio investor-profile">
            {title && <Title type={1}>{title}</Title>}

            {!investorProfile?.isEditMode ? (
                <>
                    <div className="LinkedHeader">
                        <Title type={2}>
                            {t('portfolio.createNewPortfolio.investorProfile')}
                            {' '}
                            {clientName}
                        </Title>

                        <button
                            className="button-link"
                            type="button"
                            onClick={investorProfile.showInvestorProfileEditView}
                        >
                            <span>
                                <img
                                    className="penclIicon"
                                    src={pencilIcon}
                                    alt="Edit"
                                />
                            </span>
                            {t('interaction.edit')}
                        </button>
                    </div>
                    <p>
                        {t('investorProfile.validTill')}
                        :
                        {investorProfile.expirationDate}
                    </p>
                </>
            ) : (
                <InvestorProfileBase
                    errors={isSubmitted ? investorProfile.errors : {}}
                    isLoading={investorProfile.isLoading || isSending}
                    investorProfileGroup={investorProfile.investorProfileGroup}
                    title={investorProfile.investorProfileGroup?.Name}
                    values={investorProfile.formData?.[investorProfile.groupId] || {}}
                    onChange={investorProfile.onChange}
                    data={investorProfile.investorProfileGroup}
                    t={t}
                />
            )}
        </div>
    );
};
